<script setup lang="ts">
import HeartEmpty from '@/assets/icons/2States/HeartEmpty.vue';
import HeartFull from '@/assets/icons/2States/HeartFull.vue';
import ButtonMenu from '@/assets/icons/Buttons/ButtonMenu.vue';
import ChefHatDiff from '@/assets/icons/Menus/ChefHatDiff.vue';
import { useDishes } from '@/composables/useDishes.js';
import { useEventBus } from '@/composables/useEventBus.js';
import { useOnboarding } from '@/composables/useOnboarding';
import { usePermissions } from '@/composables/usePermissions';
import type { MessageSchema } from '@/i18n/index.js';
import { useDishesStore } from '@/stores/dishesStore';
import { usePlanningStore } from '@/stores/planningStore';
import { useUserStore } from '@/stores/useUserStore';
import type { DishTag } from '@/types/api';
import type { Dish } from '@/types/dish.js';
import { calculateDishPrice } from '@/utils/prices.js';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { computed, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseCard from '../BaseComponents/BaseCard.vue';
import AddTagDialog from '../Menus/AddTagDialog.vue';
import AddDishToPlanningDialog from '../Planning/AddDishToPlanningDialog.vue';
// import { useFetch } from '@/composables/useFetch'
// import { useEnvStore } from '@/stores/envStore'
const { emit: emitBus } = useEventBus();
const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const dishesStore = useDishesStore();
const toast = useToast();
const confirm = useConfirm();
const user = ref(useUserStore().user);
const props = defineProps<{
  dish: Dish;
  selectMode?: boolean;
}>();
// const { fetchData } = useFetch()
const onboarding = useOnboarding();

const emit = defineEmits<{
  (event: 'handleDeletion', wasDeleted: boolean): void;
  (event: 'handleDuplication', duplicationResponse: null | Dish): void;
  (event: 'handleFavoriteChange', favoriteResponse: boolean | null): void;
  (event: 'openPlanDishDialog', dish: Dish): void;
  (event: 'cardClicked'): void;
}>();

const { deleteDish, duplicateDish, toggleFavorite } = useDishes();
const { can } = usePermissions();

const showAddToPlanningDialog: Ref<boolean> = ref(false);

const openAddTagToDishDialog: Ref<boolean> = ref(false);

// const selectedTags: Ref<DishTag[]> = ref([]);

const planningStore = usePlanningStore();
const popupMenu = ref();

const popUpMenuItems = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const menuItems: any[] = [];
  // Helper pour les classes désactivées (pour éviter de répéter)
  const disabledClass = onboarding.isOnboardingActive.value ? 'opacity-60 pointer-events-none cursor-default' : '';

  // Add to week menu - requires dishes_update permission
  if (can('dishes', 'update')) {
    menuItems.push({
      label: t('menuDishView.topMenu.addtoWeekMenu'),
      icon: 'pi pi-calendar',
      command: () => addToWeekMenu(),
      disabled: onboarding.isOnboardingActive.value, // Bloque la fonction
      class: disabledClass, // Bloque le visuel et le clic
    });
  }

  // Duplicate dish - requires dishes_create permission
  if (can('dishes', 'create')) {
    menuItems.push({
      label: t('menuDishView.topMenu.duplicate'),
      icon: 'pi pi-clone',
      command: () => duplicateDish(props.dish.id, 'copy').then((duplicationResponse) => emit('handleDuplication', duplicationResponse)),
      disabled: onboarding.isOnboardingActive.value, // Bloque la fonction
      class: disabledClass, // Bloque le visuel et le clic
    });
  }

  // Delete dish - requires dishes_delete permission
  if (can('dishes', 'delete')) {
    menuItems.push({
      label: t('menuDishView.topMenu.delete'),
      icon: 'pi pi-trash',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      command: (event: any) => {
        confirm.require({
          target: event.currentTarget,
          message: t('menuDishView.deleteDish.label'),
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: t('menuDishView.deleteDish.cancel'),
          rejectLabel: t('menuDishView.deleteDish.confirm'),
          accept: () => {},
          reject: () => {
            deleteDish(props.dish.id).then((wasDeleted) => emit('handleDeletion', wasDeleted));
          },
        });
      },
      disabled: onboarding.isOnboardingActive.value,
      class: disabledClass,
    });
  }

  // Add tag - requires dishes_update permission
  if (can('dishes', 'update')) {
    menuItems.push({
      label: t('menuDishView.topMenu.addToACategory'),
      icon: 'pi pi-tag',
      command: () => addTag(),
    });
  }

  // Template actions - admin only with dishes_update permission
  if (user.value?.role === 'admin' && can('dishes', 'update')) {
    menuItems.push({
      label: props.dish.is_template ? t('dish.removeTemplate') : t('dish.setTemplate'),
      icon: props.dish.is_template ? 'pi pi-star-fill' : 'pi pi-star',
      command: () => {
        useDishes().setDishTemplate(props.dish.id, toast);
      },
      disabled: onboarding.isOnboardingActive.value,
      class: disabledClass,
    });
  }

  return [{ items: menuItems }];
});

// Computed property to check if user can access any menu functionality
const canShowMenu = computed(() => {
  return can('dishes', 'update') || can('dishes', 'create') || can('dishes', 'delete') || (user.value?.role === 'admin' && can('dishes', 'update'));
});

async function addTag() {
  if (!can('dishes', 'update') || !props.dish) return;

  openAddTagToDishDialog.value = true;
}

async function updateTagsOnDish(newTagSelection: DishTag[]) {
  if (!can('dishes', 'update') || !props.dish) return;

  // Vérifiez s'il y a une différence entre la sélection actuelle et la nouvelle sélection
  const existingTags = props.dish.tags ?? [];

  const isDifferent =
    existingTags.length !== newTagSelection.length ||
    existingTags.some((existingTag) => !newTagSelection.some((newTag) => newTag.id === existingTag.id));

  // If there's no change, don't do anything.
  if (!isDifferent) {
    // console.log("Pas de changement détecté dans les tags. Aucune action nécessaire.")
    return;
  }

  try {
    const dishId = props.dish.id;
    const tagsId = newTagSelection.map((tag) => tag.id); // envoyer les IDs des tags sélectionnés

    dishesStore.updateDishTags(dishId, tagsId, toast);
  } catch (e) {
    console.error('Erreur lors de la mise à jour des tags:', e);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('menuDishView.tag.errorUpdatingTags'),
      life: 3000,
    });
  }
}

async function addToWeekMenu() {
  if (!can('dishes', 'update') || !props.dish) return;

  planningStore.dishToPlan = {
    clipboardDish: props.dish,
    selectedPlannedMenuIds: [],
    dishType: 'main',
  };

  showAddToPlanningDialog.value = true;
}

function togglePopUpMenu(event: MouseEvent) {
  if (!canShowMenu.value) return;
  popupMenu.value.toggle(event);
  emitBus('onboarding:history-menu-opened');
}

watch(openAddTagToDishDialog, (newVal) => {
  if (newVal) {
    emitBus('onboarding:choose-folder-dialog-opened');
  }
});
function handleCardClick() {
  if (!onboarding.isOnboardingActive.value) {
    emit('cardClicked');
  }
}
</script>

<template>
  <Menu
    ref="popupMenu"
    :model="popUpMenuItems"
    :popup="true"
    :pt="{ root: { class: 'onboarding-history-card-menu' }, submenuHeader: { class: '!p-0' } }"
    :pt-options="{ mergeProps: true }"
    :dismissable="!onboarding.isOnboardingActive.value"
  />
  <div class="onboarding-history-card mb-4 py-3">
    <BaseCard @click="handleCardClick">
      <div class="flex flex-row items-start justify-between gap-4">
        <div class="mb-4">
          <h3 class="line-clamp-2">{{ dish.name }}</h3>
          <ul class="flex flex-col">
            <li>
              <p class="inline text-xs">
                {{ t('history.creationDate') }}
                {{
                  new Date(dish.created_at).toLocaleString('fr-CH', {
                    dateStyle: 'short',
                  })
                }}
              </p>
            </li>
            <!-- <li>
                <h4 class="block mb-1">Ingrédients</h4>
                <IngredientsList
                gradientFrom="white"
                list-height="full"
                :ingredients="dish.ingredients"
                />
              </li> -->
          </ul>
          <p class="text-xs italic">
            <!-- {{ dish.created_using }} -->
            {{ t(`history.${dish.created_using}GeneratedDish`) }}
          </p>
        </div>
        <div v-if="!planningStore.choosingDishToPlan && !selectMode && canShowMenu" class="flex flex-row gap-2" @click.stop>
          <Button
            link
            rounded
            :pt="{ root: { class: '!p-1.5 !w-8 !h-8 rotate-90 onboarding-history-menu-button' } }"
            :pt-options="{ mergeProps: true }"
            @click="togglePopUpMenu"
          >
            <template #icon>
              <ButtonMenu />
            </template>
          </Button>
        </div>
      </div>

      <ul v-if="dish.isPlanned" class="mb-4 flex flex-col">
        <li>
          <h4 class="inline text-primary-950">{{ t('history.plannedOn') }}</h4>
        </li>
        <li v-for="(plannedDish, index) in dish.isPlanned" :key="index" class="flex flex-row gap-2 text-xs">
          <p class="truncate">
            {{
              new Date(plannedDish.date).toLocaleString('fr-CH', {
                dateStyle: 'short',
              })
            }}
            - {{ plannedDish.menu_name }}
          </p>
        </li>
      </ul>
      <div class="flex flex-row items-center justify-between">
        <div class="flex flex-row items-center gap-3">
          <div v-if="!planningStore.choosingDishToPlan && !selectMode" class="flex flex-row gap-2" @click.stop>
            <Button
              link
              rounded
              :pt="{ root: { class: '!p-1.5 !w-8 !h-8' } }"
              :pt-options="{ mergeProps: true }"
              :disabled="!can('dishes', 'update')"
              @click="toggleFavorite(props.dish.id).then((favoriteResponse) => emit('handleFavoriteChange', favoriteResponse))"
            >
              <template #icon>
                <HeartFull v-if="dish.is_favorite" />
                <HeartEmpty v-else />
              </template>
            </Button>
          </div>

          <div class="flex h-6 w-16 flex-row items-center gap-3 text-primary-950">
            <ChefHatDiff :level="dish.difficulty" />
            <!-- <ChefHatEmpty v-for="i in 4 - dish.difficulty" :key="i" /> -->
          </div>
        </div>
        <div class="px-3 py-1 font-semibold font-titles text-xs text-primary-950">
          <div>
            ~ {{ dish?.country?.currency_symbol }} {{ calculateDishPrice(dish) }} /
            {{ t('common.personShort') }}
            <!-- <h3 class="text-sm">
            </h3> -->
          </div>
        </div>
      </div>
    </BaseCard>
  </div>
  <AddDishToPlanningDialog
    v-if="can('dishes', 'update')"
    v-model:visible="showAddToPlanningDialog"
    :dish="dish"
    @close-sidebar="showAddToPlanningDialog = false"
  />
  <AddTagDialog
    v-if="openAddTagToDishDialog && can('dishes', 'update')"
    v-model:visible="openAddTagToDishDialog"
    :selected-tags="props.dish.tags ?? []"
    @update-selection="updateTagsOnDish"
  />
</template>

<style scoped></style>
